import { ROLE_LABELS, stageForRole } from './classify.js'
import { LOCAL_KEY_FILENAME } from '../../../../../packages/osteo3d-palette/index.js'

/**
 * speGeometry.js — bind the operator's files to the planning engine's report BY CONTENT.
 *
 * The planning engine's `report.json` identifies each planned mesh but cannot point at it: the doc
 * is de-identified (no paths — a case folder name carries the patient's name) and this app is a
 * hosted browser app that could not read a path anyway. The geometry travels the way everything
 * else in this tool does — the operator supplies the files — and this module is what makes that
 * SAFE: every supplied file is matched to the plan's library by the sha256 the engine's sidecar
 * stamped, because SAME NAME IS NOT THE SAME MESH. A stale skull export, or last month's splint
 * with this month's filename, hashes differently and simply does not match.
 *
 * What a match buys, in order of importance:
 *
 *   1. PROOF. The report the surgeon gets was rendered from the exact bytes the plan was made on —
 *      not from whatever happened to share a filename.
 *   2. CLASSIFICATION. The engine's library says what each mesh IS (role) and WHEN it is (stage),
 *      so a matched file no longer depends on its filename being descriptive. The engine's own
 *      workspace exports (`cranium.stl`, `maxilla_postplan.stl`) classify wrongly or not at all by
 *      name; by content they land exactly.
 *   3. A NAMED GAP LIST. Whatever the plan references that is NOT among the files is reported by
 *      label and stage — "skull-1.stl (postplan)" — so the operator knows precisely which file to
 *      go and fetch, rather than discovering a missing mesh as a skipped page.
 *
 * Matching is BY HASH ONLY — filenames play no part, the doc does not even carry real ones.
 * One file may satisfy SEVERAL library entries: a mandible the plan never moved appears as both
 * the native and the post-plan mesh with identical bytes (observed on a real case), so the match
 * is hash → entries, never hash → entry.
 *
 * Everything here is pure and synchronous except sha256Hex; the Builder owns reading File bytes.
 */

/**
 * The one file that must never travel with a case: the planning engine's de-identification key.
 * `report.json` is safe to share precisely because this file — mesh id → the ORIGINAL
 * patient-named filename — stays on the planning machine. The engine's Save deliberately does not
 * copy it, but nothing stops a hand-copy of the whole workspace, so a picked folder that contains
 * it earns a warning. The builder itself never reads the file; the hazard is the FOLDER, which the
 * operator assembled in order to move it somewhere.
 *
 * @param {string[]} fileNames
 * @returns {string|null} the warning to show, or null
 */
export function phiKeyWarning(fileNames) {
  const hit = (fileNames || []).find((n) => String(n).toLowerCase() === LOCAL_KEY_FILENAME)
  if (!hit) return null
  return `This folder contains ${LOCAL_KEY_FILENAME} — the planning engine’s key mapping meshes `
    + 'back to patient-named files. Nothing here reads or uploads it, but it should not travel '
    + 'with the case: delete it from any folder you share.'
}

/** Hex sha256 of raw bytes — the browser twin of the sidecar's `hashlib.sha256` over the file. */
export async function sha256Hex(bytes) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * The planning engine's mesh library out of a parsed report.json, or [] when the doc has none.
 * An entry is usable only when the sidecar actually hashed it — `missing: true` means the ENGINE
 * could not read that mesh when it wrote the report, which is a different fact from the operator
 * not supplying it, and the two are reported apart.
 */
export function speLibrary(doc) {
  const lib = Array.isArray(doc?.library) ? doc.library : []
  return lib.filter((m) => m && typeof m.id === 'string')
}

const readable = (e) => !e.missing && typeof e.sha256 === 'string' && e.sha256.length === 64

/**
 * Which of the built library's files are even WORTH hashing: only a file whose byte size equals
 * some readable plan entry's `bytes` can possibly hash-match. On a real case this turns "hash
 * 458 MB of meshes" into "hash the candidates", and it is exact, not heuristic — different size
 * guarantees a different hash.
 *
 * @param {Array} speLib   speLibrary(doc)
 * @param {Array} files    [{id, size}] — id is the BUILT library's mesh id, size from the File
 * @returns {string[]}     ids of files to hash
 */
export function hashCandidates(speLib, files) {
  const sizes = new Set(speLib.filter(readable).map((e) => e.bytes))
  return files.filter((f) => sizes.has(f.size)).map((f) => f.id)
}

/**
 * Match hashed files against the plan's library.
 *
 * @param {Array} speLib   speLibrary(doc)
 * @param {Array} files    [{id, name, sha256}] — sha256 null/absent for files that were not
 *                         candidates (or could not be read); they simply cannot match
 * @returns {{
 *   matched: Array<{entryId:string, label:string, role:string, stage:string, fileId:string, fileName:string}>,
 *   absent: Array<{label:string, role:string, stage:string}>,
 *   unreadable: string[],
 *   overrides: Object<string, {role:string, stage:string, as:string}>,
 *   summary: string,
 *   gaps: string[],
 * }}
 */
export function matchGeometry(speLib, files) {
  const byHash = new Map()
  for (const f of files) {
    if (typeof f?.sha256 === 'string' && !byHash.has(f.sha256)) byHash.set(f.sha256, f)
  }

  const matched = []
  const absent = []
  const unreadable = []
  const overrides = {}
  const overrideByHash = new Map()

  for (const e of speLib) {
    if (!readable(e)) {
      unreadable.push(e.name ?? e.id)
      continue
    }
    const f = byHash.get(e.sha256)
    if (!f) {
      absent.push({ label: e.name ?? e.id, role: e.role ?? 'other', stage: e.stage ?? 'native' })
      continue
    }
    matched.push({
      entryId: e.id, label: e.name ?? e.id,
      role: e.role ?? 'other', stage: e.stage ?? 'native',
      fileId: f.id, fileName: f.name,
    })
    // The classification override this match justifies. Guarded to roles this app knows: the two
    // vocabularies are shared (packages/osteo3d-palette), but a role added on the engine's side
    // first must degrade to "no override" here, not inject an unknown role into the schema.
    const role = e.role
    if (role && ROLE_LABELS[role]) {
      // The plan's stage wins outright for a matched file — content identity beats any filename
      // read — but a role whose stage is settled by the role itself (splint → output) stays
      // consistent with that rule. A LIST per hash: identical bytes can be SEVERAL library
      // entries at different stages (the unmoved mandible is native AND postplan), and each
      // supplied twin should take a DIFFERENT entry's classification, not all the first's.
      const stage = stageForRole(role) ?? e.stage ?? 'native'
      const list = overrideByHash.get(e.sha256) ?? []
      list.push({ role, stage, as: e.name ?? e.id })
      overrideByHash.set(e.sha256, list)
    }
  }

  // 🔴 EVERY file carrying a matched hash gets AN override — and each twin gets a DIFFERENT
  // library entry's classification while entries last (re-review finding: stamping the FIRST
  // entry on every twin re-staged mandible_postplan.stl as native, creating a duplicate-slot
  // conflict and an empty postplan slot). Files beyond the entry count (true Finder-copies)
  // reuse the last entry — a copy of the plan's mesh is still the plan's mesh.
  const taken = new Map()
  for (const f of files) {
    if (typeof f?.sha256 !== 'string' || overrides[f.id]) continue
    const list = overrideByHash.get(f.sha256)
    if (!list) continue
    const n = taken.get(f.sha256) ?? 0
    overrides[f.id] = list[Math.min(n, list.length - 1)]
    taken.set(f.sha256, n + 1)
  }

  const total = speLib.length
  const gaps = [
    ...absent.map((a) => `${a.label} (${a.stage}) is in the plan but not among the supplied files`),
    ...unreadable.map((n) => `${n} could not be read by the planning engine when the report was written`),
  ]
  const summary = total === 0
    ? ''
    : `${matched.length} of ${total} planned meshes matched by content`
      + (absent.length ? ` · ${absent.length} not supplied` : '')
      + (unreadable.length ? ` · ${unreadable.length} unreadable at the source` : '')

  return { matched, absent, unreadable, overrides, summary, gaps }
}
